import type Phaser from 'phaser';
import type { AppSettings, SettingsAction } from '../infrastructure/settings/settings-schema';
import { remapBinding } from '../infrastructure/settings/settings-schema';
import { AppError } from '../infrastructure/errors/app-error';
import type { ContentLoadSnapshot } from '../infrastructure/content/content-loader';
import { BattleScene } from '../presentation/scenes/phase5-scenes';
import { getPhase6Services } from './phase6-services';

export interface Phase6DebugApi {
  getSettings(): AppSettings;
  updateAudio(patch: Partial<AppSettings['audio']>): AppSettings;
  remap(player: 'p1' | 'p2', action: SettingsAction, code: string): AppSettings;
  resetSettings(): AppSettings;
  openSettings(): void;
  getLoading(): ContentLoadSnapshot;
  isLoadingRetryVisible(): boolean;
  retryLoading(): Promise<ContentLoadSnapshot>;
  isMemoryStorage(): boolean;
  isAdVisible(): boolean;
  getTraceSize(): number;
  triggerFatal(): void;
}

export const installPhase6DebugApi = (game: Phaser.Game, adElement: HTMLElement): Phase6DebugApi => {
  const active = (): Phaser.Scene | undefined => game.scene.getScenes(true)[0];
  const api: Phase6DebugApi = {
    getSettings: () => getPhase6Services().settings.get(),
    updateAudio: (patch) => getPhase6Services().settings.update({ audio: patch }),
    remap: (player, action, code) => {
      const service = getPhase6Services().settings; const next = remapBinding(service.get(), player, action, code);
      return service.update({ keyBindings: next.keyBindings });
    },
    resetSettings: () => getPhase6Services().settings.reset(),
    openSettings: () => { active()?.scene.start('Settings'); },
    getLoading: () => getPhase6Services().content.getSnapshot(),
    isLoadingRetryVisible: () => active()?.children.list.some((child) => {
      const candidate = child as { text?: unknown; visible?: unknown };
      return candidate.text === 'RETRY' && candidate.visible === true;
    }) ?? false,
    retryLoading: async () => {
      const snapshot = await getPhase6Services().content.retry();
      if (snapshot.state === 'READY') active()?.scene.start('Title');
      return snapshot;
    },
    isMemoryStorage: () => getPhase6Services().settings.usingMemoryFallback,
    isAdVisible: () => adElement.style.display !== 'none',
    getTraceSize: () => { const scene = active(); return scene instanceof BattleScene ? scene.getTrace().length : 0; },
    triggerFatal: () => getPhase6Services().fatal.show(new AppError('PRESENTATION_ERROR', 'Controlled fatal presentation failure.')),
  };
  globalThis.officeClashPhase6 = api; return api;
};
